import { useEffect, useState } from "react";
import { api } from "../../../core/lib/api";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { useToastStore } from "../../../core/stores/toast";
interface Execution {
  steps: {
    key: string;
    label: string;
    guideTitle: string;
    doneAt: number | null;
    author?: string | null;
  }[];
  consumptions: {
    id: string;
    product_name: string;
    quantity: number;
    unit: string;
    author: string;
    created_at: number;
    unit_cost_cents?: number;
  }[];
}
export function ExecutionPanel({ appointmentId }: { appointmentId: string }) {
  const [data, setData] = useState<Execution | null>(null),
    [products, setProducts] = useState<
      { id: string; name: string; unit: string; active: boolean }[]
    >([]),
    [product, setProduct] = useState(""),
    [quantity, setQuantity] = useState(""),
    [busy, setBusy] = useState(false);
  const push = useToastStore((s) => s.push),
    base = `/api/atelier/appointments/${appointmentId}/execution`;
  const load = () =>
    api<Execution>(base)
      .then(setData)
      .catch(() => push("danger", "Suivi du travail indisponible"));
  useEffect(() => {
    void load();
    void api<typeof products>("/api/atelier/products")
      .then(setProducts)
      .catch(() => {});
  }, [appointmentId]);
  const save = async (path: string, method: string, body?: unknown) => {
    if (busy) return;
    setBusy(true);
    try {
      await api(base + path, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      await load();
      setQuantity("");
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="atelier-subsection">
      <h2>Réalisation du travail</h2>
      <h3>Procédures</h3>
      {data?.steps.length ? (
        data.steps.map((s) => (
          <label
            key={s.key}
            className="atelier-check"
            style={{ alignItems: "flex-start", padding: "8px 0" }}
          >
            <input
              type="checkbox"
              checked={Boolean(s.doneAt)}
              disabled={busy}
              onChange={(e) => void save(`/steps/${s.key}`, "PUT", { done: e.target.checked })}
            />
            <span>
              <small>{s.guideTitle}</small>
              <span style={{ display: "block", whiteSpace: "pre-wrap" }}>{s.label}</span>
              {s.doneAt ? (
                <small>
                  {s.author} · {new Date(s.doneAt * 1000).toLocaleString("fr-CA")}
                </small>
              ) : null}
            </span>
          </label>
        ))
      ) : (
        <p>Aucune procédure liée aux services de ce travail.</p>
      )}
      <h3 style={{ marginTop: 24 }}>Produits réellement utilisés</h3>
      {data?.consumptions.map((c) => (
        <p key={c.id}>
          {c.product_name} · {c.quantity} {c.unit}
          {c.unit_cost_cents !== undefined
            ? ` · ${((c.quantity * c.unit_cost_cents) / 100).toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}`
            : ""}{" "}
          · {c.author}{" "}
          <Button
            size="sm"
            variant="ghost-danger"
            disabled={busy}
            onClick={() => void save(`/consumptions/${c.id}`, "DELETE")}
          >
            Retirer
          </Button>
        </p>
      ))}
      {products.some((p) => p.active) ? (
        <form
          className="form-section__grid"
          onSubmit={(e) => {
            e.preventDefault();
            void save("/consumptions", "POST", { productId: product, quantity: Number(quantity) });
          }}
        >
          <Field label="Produit">
            <Select required value={product} onChange={(e) => setProduct(e.target.value)}>
              <option value="">Choisir</option>
              {products
                .filter((p) => p.active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.unit})
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Quantité utilisée">
            <Input
              type="number"
              min="0.001"
              step="any"
              required
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={busy || !product || Number(quantity) <= 0}>
            Ajouter la consommation
          </Button>
        </form>
      ) : (
        <p>Ajoutez les consommables dans le catalogue Produits pour relever les quantités.</p>
      )}
    </section>
  );
}
